import styles from "../../styles/pages/category/CategoriesPage.module.css";
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import PageSeo from "../../components/common/PageSeo";
import JsonLd from "../../components/common/JsonLd";
import {
  buildOrganizationJsonLd,
  buildWebSiteJsonLd,
} from "../../utils/structuredData";
import { FaLayerGroup, FaSpinner } from "react-icons/fa";
import api from "../../utils/api/axiosInstance";
import getImageUrl from "../../utils/imageUtils";
import FeedMatchesTab, {
  FeedMatchesChat,
  MATCH_SHORTLIST_EXHAUSTED_PROMPT,
} from "../../components/feed/FeedMatchesTab";
import { HOME_MATCH_RESULTS_LIMIT } from "../../components/feed/FeedMatchSection";
import HelpMeDecideSection from "../../components/home/HelpMeDecideSection";
import YourDecisionCard from "../../components/home/YourDecisionCard";
import AppFeedbackPrompt from "../../components/feedback/AppFeedbackPrompt";
import ProductForYouSection from "../../components/discover/ProductForYouSection";
import useActiveFeedMatch from "../../hooks/useActiveFeedMatch";
import useHomeChatMatchResults from "../../hooks/useHomeChatMatchResults";
import { useUserAuth } from "../../contexts/UserAuthContext";
import { usePublicPages } from "../../contexts/PublicPagesContext";
import {
  getAiSessionDependencyKey,
  readHomeChatMatchCache,
  readHomeChatMatchSessionId,
} from "../../utils/homeChatMatchPersistence";
import { syncWizardFromFeedMatch } from "../../utils/resolveMatchIntent";
import { buildSearchContextSummary } from "../../utils/searchContextSummary";
import { formatSubcategoryLabel } from "../../utils/categoryDisplayUtils";

const SKELETON_CARD_COUNT = 8;

const getInitialHomeMatchState = (userId = null) => {
  const cached = readHomeChatMatchCache(userId);
  if (!cached?.products?.length) {
    return { products: [], searchParams: null, chatExpanded: true };
  }
  return {
    products: cached.products,
    searchParams: cached.searchParams ?? null,
    chatExpanded: false,
  };
};

const EMPTY_HOME_MATCH_STATE = {
  products: [],
  searchParams: null,
  chatExpanded: true,
};

const CategoryCardSkeleton = () => (
  <div className={styles.categoryCardSkeleton} aria-hidden="true">
    <div className={styles.skeletonCardHeader}>
      <span className={styles.skeletonImage} />
      <span className={styles.skeletonTitle} />
    </div>
    <div className={styles.skeletonChipRow}>
      <span className={styles.skeletonChip} />
      <span className={styles.skeletonChip} />
      <span className={styles.skeletonChip} />
      <span className={styles.skeletonChipWide} />
    </div>
  </div>
);

const CategoriesPage = () => {
  const location = useLocation();
  const { user, userAuthChecked } = useUserAuth();
  const { isPageEnabled } = usePublicPages();
  const activityUserId = user?._id || user?.id;
  const {
    activeMatch: persistedFeedMatch,
    activeDecision: persistedActiveDecision,
    loadActiveFeedMatch,
    loading: persistedFeedMatchLoading,
  } = useActiveFeedMatch(activityUserId, Boolean(activityUserId));

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadMoreRef = useRef(null);
  const categoriesRequestIdRef = useRef(0);
  const categoriesAbortRef = useRef(null);
  const isMountedRef = useRef(true);
  const [chatExpanded, setChatExpanded] = useState(true);
  const [matchCoachPrompt, setMatchCoachPrompt] = useState(null);
  const shortlistExhaustedHandledRef = useRef(false);
  const [chatMatchProducts, setChatMatchProducts] = useState([]);
  const [chatMatchSearchParams, setChatMatchSearchParams] = useState(null);
  const [chatMatchLoading, setChatMatchLoading] = useState(false);
  const [matchScoreRevision, setMatchScoreRevision] = useState(0);
  const [decisionChoice, setDecisionChoice] = useState(
    () => readHomeChatMatchCache(activityUserId)?.choice || null,
  );
  const appliedPersistedMatchKeyRef = useRef("");
  const localMatchOverrideRef = useRef(false);
  const matchStateInitializedRef = useRef(false);
  const previousActivityUserIdRef = useRef(undefined);
  const persistedMatchKey = useMemo(
    () => getAiSessionDependencyKey(persistedFeedMatch),
    [persistedFeedMatch],
  );

  useEffect(() => {
    if (!persistedFeedMatch) return;
    setDecisionChoice(persistedFeedMatch.choice || null);
  }, [persistedFeedMatch]);

  useEffect(() => {
    const syncChoice = () => {
      const cached = readHomeChatMatchCache(activityUserId);
      setDecisionChoice(cached?.choice || null);
    };
    window.addEventListener("homeChatMatchUpdated", syncChoice);
    return () => window.removeEventListener("homeChatMatchUpdated", syncChoice);
  }, [activityUserId]);

  const fetchCategories = useCallback(
    async (pageNum = 1, isLoadMore = false) => {
      const requestId = ++categoriesRequestIdRef.current;
      categoriesAbortRef.current?.abort();
      const controller = new AbortController();
      categoriesAbortRef.current = controller;

      try {
        if (pageNum === 1 && !isLoadMore) {
          setLoading(true);
        } else {
          setLoadingMore(true);
        }
        setError(null);

        const res = await api.get("/categories", {
          params: {
            activeOnly: true,
            page: pageNum,
            limit: 12,
          },
          signal: controller.signal,
        });

        if (
          !isMountedRef.current ||
          requestId !== categoriesRequestIdRef.current
        ) {
          return;
        }

        const responseData = res.data;
        const nextCategories = Array.isArray(responseData)
          ? responseData
          : responseData.categories || [];

        if (pageNum === 1) {
          setCategories(nextCategories);
        } else {
          setCategories((prev) => [...prev, ...nextCategories]);
        }

        const currentPage = responseData.pagination?.page || pageNum;
        const totalPages = responseData.pagination?.pages || 1;
        setHasMore(!Array.isArray(responseData) && currentPage < totalPages);
        setPage(pageNum);
      } catch (err) {
        if (
          !isMountedRef.current ||
          requestId !== categoriesRequestIdRef.current
        ) {
          return;
        }
        if (err.code === "ERR_CANCELED" || err.name === "CanceledError") {
          return;
        }
        console.error("Failed to fetch categories:", err);
        setError("Failed to load categories. Please try again later.");
      } finally {
        if (
          isMountedRef.current &&
          requestId === categoriesRequestIdRef.current
        ) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [],
  );

  useEffect(() => {
    isMountedRef.current = true;
    fetchCategories(1);
    return () => {
      isMountedRef.current = false;
      categoriesAbortRef.current?.abort();
    };
  }, [fetchCategories]);

  const handleRetryCategories = useCallback(() => {
    fetchCategories(1);
  }, [fetchCategories]);

  useEffect(() => {
    if (location.hash !== "#categories") return undefined;

    const scrollToCategories = () => {
      const section = document.getElementById("categories");
      section?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    if (loading) {
      return undefined;
    }

    const timer = window.setTimeout(scrollToCategories, 100);
    return () => window.clearTimeout(timer);
  }, [location.hash, loading]);

  useEffect(() => {
    if (!userAuthChecked) return;

    if (previousActivityUserIdRef.current !== activityUserId) {
      const isUserSwitch = previousActivityUserIdRef.current !== undefined;
      previousActivityUserIdRef.current = activityUserId;
      appliedPersistedMatchKeyRef.current = "";
      localMatchOverrideRef.current = false;

      if (isUserSwitch) {
        if (activityUserId) {
          setChatMatchProducts([]);
          setChatMatchSearchParams(null);
          setChatExpanded(true);
        } else {
          const guestState = getInitialHomeMatchState(null);
          setChatMatchProducts(guestState.products);
          setChatMatchSearchParams(guestState.searchParams);
          setChatExpanded(guestState.chatExpanded);
          if (guestState.products.length) {
            syncWizardFromFeedMatch(readHomeChatMatchCache(null));
            setMatchScoreRevision((n) => n + 1);
          }
        }
      }
    }

    if (matchStateInitializedRef.current) return;
    matchStateInitializedRef.current = true;

    const initialState = activityUserId
      ? EMPTY_HOME_MATCH_STATE
      : getInitialHomeMatchState(null);
    setChatMatchProducts(initialState.products);
    setChatMatchSearchParams(initialState.searchParams);
    setChatExpanded(initialState.chatExpanded);
    if (initialState.products.length) {
      syncWizardFromFeedMatch(readHomeChatMatchCache(null));
      setMatchScoreRevision((n) => n + 1);
    }
  }, [activityUserId, userAuthChecked]);

  useEffect(() => {
    if (!persistedFeedMatch?.products?.length || !persistedMatchKey) return;

    if (localMatchOverrideRef.current) {
      return;
    }

    if (appliedPersistedMatchKeyRef.current === persistedMatchKey) {
      return;
    }

    appliedPersistedMatchKeyRef.current = persistedMatchKey;
    setChatMatchProducts(persistedFeedMatch.products);
    setChatMatchSearchParams(persistedFeedMatch.searchParams ?? null);
    syncWizardFromFeedMatch(persistedFeedMatch);
    setMatchScoreRevision((n) => n + 1);
    setChatExpanded(false);
  }, [persistedMatchKey, persistedFeedMatch]);

  useEffect(() => {
    if (!userAuthChecked || !activityUserId) return;
    if (persistedFeedMatchLoading) return;
    if (persistedFeedMatch?.products?.length) return;
    if (localMatchOverrideRef.current) return;
    // Keep local shortlist + collapsed chat after a fresh match while the
    // server active-match catch-up is still empty.
    if (chatMatchProducts.length > 0) return;

    setChatMatchProducts([]);
    setChatMatchSearchParams(null);
    setChatExpanded(true);
  }, [
    activityUserId,
    persistedFeedMatch,
    persistedFeedMatchLoading,
    userAuthChecked,
    chatMatchProducts.length,
  ]);

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore && !loading) {
      fetchCategories(page + 1, true);
    }
  }, [fetchCategories, hasMore, loading, loadingMore, page]);

  useEffect(() => {
    if (!loadMoreRef.current || !hasMore || loading) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !loadingMore && hasMore) {
          loadMore();
        }
      },
      {
        rootMargin: "100px",
        threshold: 0.1,
      },
    );

    observer.observe(loadMoreRef.current);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, loadingMore, loading, loadMore]);

  const handleChatMatchResults = useHomeChatMatchResults({
    activityUserId,
    loadActiveFeedMatch,
    setChatMatchLoading,
    setChatMatchSearchParams,
    setChatMatchProducts,
    setMatchScoreRevision,
    setChatExpanded,
    variant: "home",
    onAfterApply: ({
      sessionId: resolvedSessionId,
      products: resolvedProducts,
      searchParams: resolvedSearchParams,
      serverMatch,
    }) => {
      const appliedKey = getAiSessionDependencyKey({
        sessionId: resolvedSessionId,
        products: resolvedProducts,
        searchParams: resolvedSearchParams,
      });
      appliedPersistedMatchKeyRef.current = appliedKey;
      localMatchOverrideRef.current =
        Boolean(activityUserId && resolvedProducts.length) &&
        !serverMatch?.products?.length;
    },
  });

  useEffect(() => {
    shortlistExhaustedHandledRef.current = false;
    setMatchCoachPrompt(null);
  }, [chatMatchProducts]);

  const handleShortlistExhausted = useCallback(() => {
    if (shortlistExhaustedHandledRef.current) return;
    shortlistExhaustedHandledRef.current = true;
    setChatExpanded(true);
    setMatchCoachPrompt(MATCH_SHORTLIST_EXHAUSTED_PROMPT);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleCoachPromptConsumed = useCallback(() => {
    setMatchCoachPrompt(null);
  }, []);

  const showingMatchResults = chatMatchLoading || chatMatchProducts.length > 0;
  const matchContextSummary = buildSearchContextSummary(chatMatchSearchParams);
  const hasMultipleMatches = chatMatchProducts.length > 1;
  const showContinuedResults = chatMatchProducts.length > 0;
  const rankedMatchProducts = useMemo(() => {
    const sorted = [...chatMatchProducts].sort((a, b) => {
      const scoreA = Number(a.matchScore ?? a.score ?? -Infinity);
      const scoreB = Number(b.matchScore ?? b.score ?? -Infinity);
      if (Number.isFinite(scoreB) && Number.isFinite(scoreA) && scoreB !== scoreA) {
        return scoreB - scoreA;
      }
      const rankA = a.rank != null ? Number(a.rank) : 99;
      const rankB = b.rank != null ? Number(b.rank) : 99;
      if (rankA !== rankB) return rankA - rankB;
      const idA = String(a._id || a.id || "");
      const idB = String(b._id || b.id || "");
      if (idA === idB) return 0;
      return idA < idB ? -1 : 1;
    });
    return sorted.slice(0, HOME_MATCH_RESULTS_LIMIT);
  }, [chatMatchProducts]);

  return (
    <>
      <PageSeo
        title="Simpary — Find products that fit"
        description="Describe what you're shopping for. Simpary searches the catalog, ranks the closest matches, and lets you browse by category."
        pathname="/"
      />
      <JsonLd data={[buildOrganizationJsonLd(), buildWebSiteJsonLd()]} />
      <div className="pageContainer">
        <div className={`innerContainer ${styles.homePageStack}`}>
          {error && (
            <div className={styles.errorState} role="alert">
              <p>{error}</p>
              <button
                type="button"
                className={styles.retryButton}
                onClick={handleRetryCategories}
              >
                Retry
              </button>
            </div>
          )}
          <section
            className={`${styles.chatHero} ${
              showingMatchResults ? styles.chatHeroWithResults : ""
            } ${showContinuedResults ? styles.chatHeroPersonalizedBlock : ""}`.trim()}
            aria-labelledby={
              showingMatchResults
                ? "home-hero-results-title"
                : "home-hero-title"
            }
            id={
              showContinuedResults
                ? "home-personalized-recommendation"
                : undefined
            }
          >
            <div
              className={`${styles.chatHeroInner} ${
                showingMatchResults ? styles.chatHeroInnerCompact : ""
              }`.trim()}
            >
              {!showingMatchResults ? (
                <div className={styles.chatHeroCopy}>
                  <h1 id="home-hero-title" className={styles.chatHeroTitle}>
                    What are you shopping for?
                  </h1>
                  <p className={styles.chatHeroSubtitle}>
                    Name the product, your budget, and how you&apos;ll use it.
                    We&apos;ll search the catalog.
                  </p>
                </div>
              ) : (
                <div className={styles.chatHeroAnchor}>
                  <h1
                    id="home-hero-results-title"
                    className={styles.chatHeroAnchorTitle}
                  >
                    Best match
                  </h1>
                  {matchContextSummary && (
                    <p className={styles.chatHeroAnchorContext}>
                      Based on: {matchContextSummary}
                    </p>
                  )}
                </div>
              )}

              <div className={styles.chatHeroChat}>
                {decisionChoice && showContinuedResults && (
                  <YourDecisionCard
                    choice={decisionChoice}
                    products={rankedMatchProducts}
                    searchParams={chatMatchSearchParams}
                  />
                )}
                <FeedMatchesChat
                  chatExpanded={chatExpanded}
                  onChatExpandedChange={setChatExpanded}
                  onMatchResults={handleChatMatchResults}
                  chatCardClassName={styles.chatHeroCard}
                  collapsedBarClassName={styles.chatHeroCollapsed}
                  collapsedTitleClassName={styles.chatHeroCollapsedTitle}
                  collapsedHintClassName={styles.chatHeroCollapsedHint}
                  collapsedIconClassName={styles.chatHeroCollapsedIcon}
                  collapseButtonClassName={styles.chatHeroCollapseBtn}
                  panelVariant="landing"
                  hasResults={showingMatchResults && !chatMatchLoading}
                  searchParams={chatMatchSearchParams}
                  showCollapsedContextSummary={false}
                  coachPrompt={matchCoachPrompt}
                  onCoachPromptConsumed={handleCoachPromptConsumed}
                />

                {showingMatchResults && (
                  <FeedMatchesTab
                    active
                    products={chatMatchProducts}
                    matchLoading={chatMatchLoading || persistedFeedMatchLoading}
                    searchParams={chatMatchSearchParams}
                    matchScoreRevision={matchScoreRevision}
                    activeDecision={persistedActiveDecision}
                    className={styles.chatHeroFeaturedMatch}
                    variant="hero"
                    mode="featured"
                    onShortlistExhausted={handleShortlistExhausted}
                  />
                )}

                {showContinuedResults && hasMultipleMatches && (
                  <a
                    href="#home-match-results"
                    className={styles.chatHeroSeeAll}
                  >
                    Top {chatMatchProducts.length} matches
                  </a>
                )}
              </div>
            </div>
          </section>

          {showContinuedResults && (
            <>
              {hasMultipleMatches && (
                <HelpMeDecideSection
                  products={rankedMatchProducts}
                  searchParams={chatMatchSearchParams}
                  matchScoreRevision={matchScoreRevision}
                  activeDecision={persistedActiveDecision}
                  choice={decisionChoice}
                  sessionId={
                    persistedFeedMatch?.sessionId || readHomeChatMatchSessionId()
                  }
                  ownerUserId={activityUserId || null}
                  isGuest={!activityUserId}
                />
              )}

              <AppFeedbackPrompt
                source="wizard"
                prompt="How are your AI match results working for you?"
                metadata={{
                  resultCount: chatMatchProducts.length,
                  sessionId: readHomeChatMatchSessionId(),
                }}
              />
            </>
          )}

          {isPageEnabled("forYou") && <ProductForYouSection />}

          <section
            id="categories"
            className={`${styles.categoriesSection} ${
              showContinuedResults ? styles.categoriesSectionExplore : ""
            }`.trim()}
            aria-labelledby="categories-section-title"
          >
            {!showContinuedResults && (
              <div className={styles.categoriesPanel}>
                <header className={styles.categoriesSectionHeader}>
                  <FaLayerGroup
                    className={styles.categoriesSectionIcon}
                    aria-hidden="true"
                  />
                  <div>
                    <h2
                      id="categories-section-title"
                      className={styles.categoriesSectionTitle}
                    >
                      Browse categories
                    </h2>
                    <p className={styles.categoriesSectionSubtitle}>
                      Phones, laptops, TVs, and more
                    </p>
                  </div>
                </header>
              </div>
            )}

            {showContinuedResults && (
              <header className={styles.categoriesExploreHeader}>
                <FaLayerGroup
                  className={styles.categoriesSectionIcon}
                  aria-hidden="true"
                />
                <div>
                  <h2
                    id="categories-section-title"
                    className={styles.categoriesSectionTitle}
                  >
                    Browse categories
                  </h2>
                  <p className={styles.categoriesSectionSubtitle}>
                    Phones, laptops, TVs, and more
                  </p>
                </div>
              </header>
            )}

            {loading ? (
              <div
                className={styles.contentWrapper}
                aria-busy="true"
                aria-label="Loading categories"
              >
                {Array.from({ length: SKELETON_CARD_COUNT }).map((_, i) => (
                  <CategoryCardSkeleton key={i} />
                ))}
              </div>
            ) : error ? null : categories.length === 0 ? (
              <div className={styles.emptyState}>
                <FaLayerGroup className={styles.emptyIcon} />
                <h3>No Categories Available</h3>
                <p>There are no categories to display at the moment.</p>
              </div>
            ) : (
              <>
                <div className={styles.contentWrapper}>
                  {categories.map((cat) => {
                    const activeSubcategories = (
                      cat.subcategories || []
                    ).filter((sub) => sub.isActive !== false);

                    return (
                      <div key={cat._id} className={styles.categoryCard}>
                        <Link
                          to={`/category/${cat._id}`}
                          className={styles.cardHeader}
                        >
                          <div className={styles.cardImageWrapper}>
                            <img
                              src={getImageUrl(cat.categoryImage)}
                              alt={cat.name || "Category"}
                              className={styles.cardImage}
                              onError={(e) => {
                                e.currentTarget.src = "/fallback.png";
                                e.currentTarget.onerror = null;
                              }}
                            />
                          </div>
                          <h3 className={styles.cardTitle}>
                            {cat.name
                              ? cat.name.charAt(0).toUpperCase() +
                                cat.name.slice(1)
                              : "Unnamed Category"}
                          </h3>
                        </Link>

                        <div className={styles.subcategoryList}>
                          {activeSubcategories.length > 0 ? (
                            activeSubcategories.slice(0, 6).map((sub) => (
                              <Link
                                key={sub._id || sub.slug}
                                to={`/category/${cat._id}?sub=${encodeURIComponent(
                                  sub.slug,
                                )}`}
                                className={styles.subcategoryItem}
                              >
                                {formatSubcategoryLabel(sub.name)}
                              </Link>
                            ))
                          ) : (
                            <div className={styles.noSubcategories}>
                              No subcategories available
                            </div>
                          )}
                        </div>
                        {activeSubcategories.length > 0 && (
                          <Link
                            to={`/category/${cat._id}`}
                            className={styles.viewAllLink}
                          >
                            View All Subcategories →
                          </Link>
                        )}
                      </div>
                    );
                  })}

                  {loadingMore && (
                    <div className={styles.loadingMore}>
                      <FaSpinner className={styles.spinner} />
                      <p>Loading more categories...</p>
                    </div>
                  )}

                  <div ref={loadMoreRef} className={styles.loadMoreTrigger} />
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </>
  );
};

export default CategoriesPage;
